import React, { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import Header from "../../layout/Header/Header";
import {
  Button,
  Row,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
  ButtonGroup,
  Col,
} from "reactstrap";
import Swal from "sweetalert2";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";
import axios from "axios";
import { TableHeader, Pagination, Search } from "../../DataTable";
import useFullPageLoader from "../../hooks/useFullPageLoader";

const MESES_TXT = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const mesLabel = (m) => {
  if (!m || !String(m).includes("-")) return m;
  const [y, mm] = String(m).split("-");
  return `${MESES_TXT[parseInt(mm, 10) - 1]}-${y.slice(2)}`;
};

function ReporteMasterCostos() {
  const { user } = useContext(AuthContext);

  const URL_MASTER_COSTOS = process.env.REACT_APP_URL_MASTER_COSTOS;
  const URL_SEND_MAIL_ATT = process.env.REACT_APP_URL_SEND_MAIL_ATT;
  const puedeVerCostos = user.inventarios_costos;

  const [meses, setMeses] = useState([]);
  const [existenMovs, setExistenMovs] = useState(true);
  const [nuevoMes, setNuevoMes] = useState("");
  const [selectedCategoria, setSelectedCategoria] = useState("");

  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 1100,
    timerProgressBar: true,
  });

  const [modalMail, setModalMail] = useState(false);
  const toggleMail = () => setModalMail(!modalMail);
  const [mailTo, setMailTo] = useState("");

  const [comments, setComments] = useState([]);
  const [loader, showLoader, hideLoader] = useFullPageLoader();
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState({ field: "", order: "" });

  const ITEMS_PER_PAGE = 50;

const formatNumber = (value) =>
  new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

  function cargar() {
    showLoader();
    axios
      .get(URL_MASTER_COSTOS, {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      })
      .then((response) => {
        const allData = response.data?.data || [];
        const allMeses = response.data?.meses || [];

        setComments(allData);
        setMeses(allMeses);
        setExistenMovs(allData.length > 0);
        setTotalItems(allData.length);

        hideLoader();
      })
      .catch((err) => {
        console.log(err);
        hideLoader();

        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: "No se pudo cargar el reporte",
        });
      });
  }

  useEffect(() => {
    cargar();
  }, []);

  // Actualiza el valor local mientras escribe
  function handleChange(idArticulo, mes, value) {
    setComments((prev) =>
      prev.map((row) =>
        row.idArticulo === idArticulo
          ? { ...row, costos: { ...row.costos, [mes]: value } }
          : row
      )
    );
  }

  // Guarda el costo al salir de la celda
  function handleSave(idArticulo, mes, value) {
    axios
      .post(
        URL_MASTER_COSTOS,
        { idArticulo, mesYAnio: mes, costoMX: Number(value) || 0 },
        { headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` } }
      )
      .then(() => Toast.fire({ icon: "success", title: "Costo guardado" }))
      .catch((err) => {
        console.log(err);
        Swal.fire({ icon: "error", title: "Oops...", text: "No se pudo guardar" });
      });
  }

  function agregarMes() {
    if (!nuevoMes) return;
    if (!meses.includes(nuevoMes)) {
      setMeses([...meses, nuevoMes].sort().reverse());
    }
    setNuevoMes("");
  }

  const headers = useMemo(() => {
    return [
      { name: "Item", field: "item", sortable: true },
      { name: "Nombre", field: "nombre", sortable: true },
      ...meses.map((mes) => ({
        name: mes,
        field: mes,
        sortable: true,
      })),
    ];
  }, [meses]);

  const commentsFiltrados = useMemo(() => {
    let computedComments = [...comments];

    if (selectedCategoria) {
      computedComments = computedComments.filter(
        (comment) => String(comment.categoria || "") === selectedCategoria
      );
    }

    if (search) {
      const searchLower = search.toLowerCase();

      computedComments = computedComments.filter((comment) => {
        const baseMatch =
          String(comment.item || "").toLowerCase().includes(searchLower) ||
          String(comment.nombre || "").toLowerCase().includes(searchLower);

        const mesesMatch = meses.some((mes) =>
          String(comment.costos?.[mes] || "").includes(search)
        );

        return baseMatch || mesesMatch;
      });
    }

    if (sorting.field) {
      const reversed = sorting.order === "asc" ? 1 : -1;

      computedComments.sort((a, b) => {
        if (meses.includes(sorting.field)) {
          return (
            reversed *
            (Number(a.costos?.[sorting.field] || 0) -
              Number(b.costos?.[sorting.field] || 0))
          );
        }

        return (
          reversed *
          String(a[sorting.field] || "").localeCompare(
            String(b[sorting.field] || ""),
            "es",
            { sensitivity: "base" }
          )
        );
      });
    }

    setTotalItems(computedComments.length);

    return computedComments;
  }, [comments, search, sorting, meses, selectedCategoria]);

  // Lista de categorias (lineas) unicas para el filtro
  const categorias = useMemo(() => {
    const set = new Set(comments.map((c) => c.categoria).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b, "es"));
  }, [comments]);

  const commentsData = useMemo(() => {
    return commentsFiltrados.slice(
      (currentPage - 1) * ITEMS_PER_PAGE,
      (currentPage - 1) * ITEMS_PER_PAGE + ITEMS_PER_PAGE
    );
  }, [commentsFiltrados, currentPage]);

  function excel() {
    const dataExcel = commentsFiltrados.map((a) => {
      const row = {
        Item: a.item || "",
        Nombre: a.nombre || "",
      };

      meses.forEach((mes) => {
        row[mes] = Number(a.costos?.[mes] || 0);
      });

      return row;
    });

    const option = {
      fileName: "MasterCostos",
      datas: [
        {
          sheetData: dataExcel,
          sheetName: "MasterCostos",
          sheetFilter: ["Item", "Nombre", ...meses],
          sheetHeader: ["Item", "Nombre", ...meses.map(mesLabel)],
        },
      ],
    };

    const toExcel = new ExportJsonExcel(option);
    toExcel.saveExcel();
  }

  function PDFTabla() {
    const doc = new jsPDF("landscape", "pt", "letter");

    const body = commentsFiltrados.map((a) => [
      a.item || "",
      a.nombre || "",
      ...meses.map((mes) => formatNumber(a.costos?.[mes] || 0)),
    ]);

    doc.setFontSize(14);
    doc.text("Master Costos", 40, 35);

    doc.autoTable({
      head: [["Item", "Nombre", ...meses.map(mesLabel)]],
      body,
      startY: 55,
      styles: {
        fontSize: 7,
        cellPadding: 3,
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
      },
      columnStyles: {
        0: {cellWidth: 45 },
        1: { halign: "left", cellWidth: 140 },
      },
    });

    window.open(doc.output("bloburl"), "_blank");
  }

  function enviaMail() {
    const doc = new jsPDF("landscape", "pt", "letter");

    const body = commentsFiltrados.map((a) => [
      a.item || "",
      a.nombre || "",
      ...meses.map((mes) => formatNumber(a.costos?.[mes] || 0)),
    ]);

    doc.setFontSize(14);
    doc.text("Master Costos", 40, 35);

    doc.autoTable({
      head: [["Item", "Nombre", ...meses.map(mesLabel)]],
      body,
      startY: 55,
      styles: {
        fontSize: 7,
        cellPadding: 3,
        halign: "right",
      },
      columnStyles: {
        0: { halign: "right", cellWidth: 45 },
        1: { halign: "left", cellWidth: 140 },
      },
    });

    const att = doc.output("datauristring").split(",")[1];

    axios
      .post(
        URL_SEND_MAIL_ATT,
        {
          subject: "Master Costos",
          email: mailTo,
          fileName: "MasterCostos.pdf",
          att,
          text: `<table border="0" cellpadding="0" cellspacing="0" width="100%">
            <tr style="font-size:20px;font-family:Helvetica,Arial,sans-serif;color:#333333;font-weight:bold">
              <td>Hola,</td>
            </tr>
            <tr style="font-size:20px;font-family:Helvetica,Arial,sans-serif;color:#333333;font-weight:bold">
              <td>Adjunto encontrarás el reporte Master Costos.</td>
            </tr>
          </table>`,
        },
        {
          headers: {
            Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
          },
        }
      )
      .then(() => {
        toggleMail();
        Swal.fire("Good job!", "Enviado con éxito", "success");
      })
      .catch((error) => {
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: "Something went wrong!",
          footer: `${error.response?.data || ""}`,
        });
        console.log(error);
      });
  }

return (
  <>
    <Header />
    <br />
    <br />

    {user.menu_pedidos ? (
      <div className="card col-12">
        <Row>
          <Col md={8}>
            <Button
              size="sm"
              href="/ReportesBI"
              className="btn btn-danger"
              id="botonListado"
            >
              Regresar
            </Button>
          </Col>

          <Col sm={4}>
            <ButtonGroup id="logoutBoton">
              <Button
                size="sm"
                className="btn"
                color="danger"
                onClick={PDFTabla}
                disabled={commentsFiltrados.length === 0}
              >
                PDF <i className="far fa-file-pdf"></i>
              </Button>

              <Button
                size="sm"
                className="btn"
                color="info"
                onClick={toggleMail}
                disabled={commentsFiltrados.length === 0}
              >
                eMail <i className="fas fa-at"></i>
              </Button>

              <Button
                size="sm"
                className="btn"
                color="primary"
                onClick={excel}
                disabled={commentsFiltrados.length === 0}
              >
                Excel <i className="far fa-file-excel"></i>
              </Button>
            </ButtonGroup>
          </Col>
        </Row>

        <Modal size="sm" isOpen={modalMail} toggle={toggleMail}>
          <ModalHeader toggle={toggleMail}>
            <h4>Enviar Master Costos</h4>
          </ModalHeader>

          <ModalBody>
            <Label className="mr-sm-2">Email</Label>
            <Input
              className="col-sm-12"
              type="text"
              value={mailTo}
              required
              onChange={(e) => {
                setMailTo(e.target.value);
              }}
            />
          </ModalBody>

          <ModalFooter>
            <Button size="sm" color="success" onClick={enviaMail}>
              Enviar
            </Button>
          </ModalFooter>
        </Modal>

        <h3 align="center">Master Costos</h3>
        <p align="center"><small>Captura el costo de cada producto por mes. Al iniciar un mes nuevo se arrastra el ultimo costo del mes anterior.</small></p>

        <Row className="align-items-end">
          {/* <Col md={3}>
            <Label className="mr-sm-2">Agregar mes</Label>
            <Input
              bsSize="sm"
              type="month"
              value={nuevoMes}
              onChange={(e) => setNuevoMes(e.target.value)}
            />
          </Col>
          <Col md={2}>
            <Button size="sm" color="info" onClick={agregarMes}>
              Agregar columna
            </Button>
          </Col> */}
          <Col md={2}>
            <Button size="sm" color="secondary" onClick={cargar}>
              Guardar
            </Button>
          </Col>
          <Col md={3}>
            <Label className="mr-sm-2">Categoria</Label>
            <Input
              bsSize="sm"
              type="select"
              value={selectedCategoria}
              onChange={(e) => {
                setSelectedCategoria(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">Todas</option>
              {categorias.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Input>
          </Col>
        </Row>
        <br />

        <div className="row">
          <div className="col-md-8">
            <Pagination
              total={totalItems}
              itemsPerPage={ITEMS_PER_PAGE}
              currentPage={currentPage}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>

          <div className="col-md-4 d-flex flex-row-reverse">
            <Search
              onSearch={(value) => {
                setSearch(value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>

        <br />

        <div className="table-wrapper">
          <Table size="sm" striped borderless className="table-responsive-xl">
            <thead>
              <tr>
                <th colSpan="2"></th>
                <th colSpan={meses.length} className="text-center">
                  Costo
                </th>
              </tr>

              <tr>
                <th>SKU</th>
                <th>Nombre</th>
                {meses.map((mes) => (
                  <th key={mes} style={{ textAlign: "center", minWidth: "95px" }}>
                    {mesLabel(mes)}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {existenMovs ? (
                commentsData.map((a) => {
                  return (
                    <tr key={a.idArticulo || a.item}>
                      <td style={{ fontWeight: "500" }}>{a.item}</td>
                      <td>{a.nombre}</td>

                      {meses.map((mes) => (
                        <td key={`${a.item}-${mes}`} style={{ minWidth: "95px" }}>
                          <Input
                            bsSize="sm"
                            type="number"
                            step="0.01"
                            value={a.costos?.[mes] ?? ""}
                            onChange={(e) => handleChange(a.idArticulo, mes, e.target.value)}
                            onBlur={(e) => handleSave(a.idArticulo, mes, e.target.value)}
                          />
                        </td>
                      ))}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={2 + meses.length} align="center">
                    No se encontraron productos...
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        <div className="col-md-6">
          <Pagination
            total={totalItems}
            itemsPerPage={ITEMS_PER_PAGE}
            currentPage={currentPage}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </div>
      </div>
    ) : undefined}

    {loader}
  </>
);
}

export default ReporteMasterCostos;